import { Lock, ArrowUpRight } from 'lucide-react';
import { useEntitlement } from '../context/EntitlementContext';
import { PLANS } from '../config/plans';
import type { ReactNode } from 'react';

interface UpgradeGateProps {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export default function UpgradeGate({ feature, children, fallback }: UpgradeGateProps) {
  const { canUse, data } = useEntitlement();

  if (canUse(feature)) return <>{children}</>;

  const currentPlan = data?.plan || 'starter';
  const currentIdx = PLANS.findIndex(p => p.id === currentPlan);
  const nextPlan = PLANS[currentIdx + 1];

  if (fallback) return <>{fallback}</>;

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Lock className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
          Feature Not Available
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          <span className="capitalize font-medium">{feature.replace(/_/g, ' ')}</span> is not included in your <span className="font-bold text-primary">{currentPlan}</span> plan.
          {nextPlan && <> Upgrade to <span className="font-bold">{nextPlan.name}</span> to unlock this feature.</>}
        </p>
        {nextPlan && (
          <button onClick={() => { window.location.hash = '#plan-billing'; }}
            className="bg-primary text-white rounded-xl px-6 py-2.5 text-sm font-bold flex items-center gap-2 mx-auto hover:opacity-90 transition shadow-lg shadow-primary/20">
            <ArrowUpRight className="w-4 h-4" /> Upgrade to {nextPlan.name}
          </button>
        )}
      </div>
    </div>
  );
}
